import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "../../utils/axiosAuth";
import { IUserAuth } from "src/@types/auth";

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    nome: string;
    telefone: string;
    email: string;
    cpf: string;
    endereco: string;
    dataNascimento: string;
  };
}

interface RecoveryPassword {
  email: string;
}

interface NewPassword {
  accessToken: string;
}

export const postLogin: any = createAsyncThunk<
  LoginResponse,
  { email: string; password: string }
>("auth/postLogin", async ({ email, password }) => {
  const response = await axios.post("/auth/user/login", {
    email,
    senha: password,
  });
  return response.data;
});

export const postRecoveryPassword: any = createAsyncThunk<
  RecoveryPassword,
  { email: string }
>("auth/postRecoveryPassword", async ({ email }) => {
  const response = await axios.post("/auth/recovery", { email });
  return response.data;
});

export const postNewPassword: any = createAsyncThunk<
  RecoveryPassword,
  { hash: string; newPassword: string }
>("auth/postNewPassword", async ({ hash, newPassword }) => {
  const response = await axios.post("/auth/setpass", {
    recoveryHash: hash,
    newPassword: newPassword,
  });
  return response.data;
});

export const postRefreshToken: any = createAsyncThunk<
  NewPassword,
  { refreshToken: string }
>("auth/postRefreshToken", async ({ refreshToken }) => {
  const response = await axios.post("/auth/user/refresh", {
    refreshToken: refreshToken,
  });
  return response.data;
});

const initialState: IUserAuth = {
  accessToken: "",
  refreshToken: "",
  userReturn: {
    name: "",
    email: "",
    picture: "",
    status: "",
    phone: "",
    userType: "",
  },
  isLoading: false,
  error: undefined,
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    logout: (state) => {
      state.accessToken = "";
      state.refreshToken = "";
      state.userReturn = initialState.userReturn;
      state.isLoading = false;
      state.error = "";
      state.messageRecovery = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // ExtraReducers para postLogin
      .addCase(postLogin.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(postLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.userReturn = {
          ...state.userReturn,
          name: action.payload.user.nome,
          email: action.payload.user.email,
          phone: action.payload.user.telefone,
        };
        state.error = undefined;
      })
      .addCase(postLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = "Error ao fazer login";
        state.accessToken = "";
        state.refreshToken = "";
        state.userReturn = initialState.userReturn;
      })
      .addCase(postRecoveryPassword.pending, (state) => {
        state.isLoading = true;
        state.messageRecovery = "";
      })
      .addCase(postRecoveryPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.messageRecovery = "E-mail enviado!";
      })
      .addCase(postRecoveryPassword.rejected, (state) => {
        state.isLoading = false;
        state.messageRecovery = "Erro ao enviar e-mail!";
      })
      .addCase(postNewPassword.pending, (state) => {
        state.isLoading = true;
        state.messageRecovery = "";
      })
      .addCase(postNewPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.messageRecovery = "senha alterada";
      })
      .addCase(postNewPassword.rejected, (state) => {
        state.isLoading = false;
        state.messageRecovery = "Erro ao alterar senha";
      })
      .addCase(postRefreshToken.pending, (state) => {
        state.isLoading = true;
        state.messageRecovery = "";
      })
      .addCase(postRefreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.accessToken;
      })
      .addCase(postRefreshToken.rejected, (state) => {
        state.accessToken = "";
        state.refreshToken = "";
        state.userReturn = initialState.userReturn;
        state.isLoading = false;
        state.error = "";
        state.messageRecovery = "";
      });
  },
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
